<template>
  <div class="tiandi-map" :style="{ height: height }">
    <div v-if="showToolbar" class="tiandi-map__toolbar">
      <div class="tiandi-map__actions">
        <el-button
          type="primary"
          icon="el-icon-edit-outline"
          :disabled="loading || !mapReady"
          @click="enableDrawPolygon"
        >
          画区域
        </el-button>
        <el-button
          icon="el-icon-delete"
          :disabled="loading || !mapReady"
          @click="clearLayer"
        >
          清除
        </el-button>
      </div>

      <el-form
        :inline="true"
        :model="coordinateForm"
        class="tiandi-map__form"
        @submit.native.prevent
      >
        <el-form-item label="经度">
          <el-input
            v-model.trim="coordinateForm.longitude"
            clearable
            placeholder="如 11858.995000"
          />
        </el-form-item>
        <el-form-item label="纬度">
          <el-input
            v-model.trim="coordinateForm.latitude"
            clearable
            placeholder="如 3911.567311"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-location"
            :disabled="loading || !mapReady"
            @click="addMarkerByForm"
        >
            确定
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="tiandi-map__body" v-loading="loading">
      <div ref="mapContainer" class="tiandi-map__container"></div>
      <div v-if="showOverlayAdjuster" class="tiandi-map__overlay-panel">
        <div class="tiandi-map__overlay-header">
          <span>厂区图校准</span>
          <el-switch
            v-model="overlayForm.visible"
            :disabled="!mapReady"
          />
        </div>
        <el-form
          class="tiandi-map__overlay-form"
          label-width="70px"
          size="mini"
          @submit.native.prevent
        >
          <el-form-item label="中心经度">
            <el-input-number
              v-model="overlayForm.centerLng"
              :disabled="!mapReady"
              controls-position="right"
              :precision="10"
              :step="0.00001"
            />
          </el-form-item>
          <el-form-item label="中心纬度">
            <el-input-number
              v-model="overlayForm.centerLat"
              :disabled="!mapReady"
              controls-position="right"
              :precision="10"
              :step="0.00001"
            />
          </el-form-item>
          <el-form-item label="图片宽度">
            <el-input-number
              v-model="overlayForm.width"
              :disabled="!mapReady"
              controls-position="right"
              :min="0.000001"
              :precision="8"
              :step="0.0001"
            />
          </el-form-item>
          <el-form-item label="图片高度">
            <el-input-number
              v-model="overlayForm.height"
              :disabled="!mapReady"
              controls-position="right"
              :min="0.000001"
              :precision="8"
              :step="0.0001"
            />
          </el-form-item>
          <el-form-item label="微调步长">
            <el-input-number
              v-model="overlayForm.nudgeStep"
              :disabled="!mapReady"
              controls-position="right"
              :min="0.000001"
              :precision="8"
              :step="0.00001"
            />
          </el-form-item>
          <el-form-item label="透明度">
            <el-slider
              v-model="overlayForm.opacity"
              :disabled="!mapReady"
              :min="0"
              :max="1"
              :step="0.05"
            />
          </el-form-item>
          <div class="tiandi-map__overlay-tools">
            <el-tooltip content="向上移动" placement="top">
              <el-button
                icon="el-icon-arrow-up"
                :disabled="!mapReady"
                @click="moveOverlay('north')"
              />
            </el-tooltip>
            <el-tooltip content="向左移动" placement="top">
              <el-button
                icon="el-icon-arrow-left"
                :disabled="!mapReady"
                @click="moveOverlay('west')"
              />
            </el-tooltip>
            <el-tooltip content="向右移动" placement="top">
              <el-button
                icon="el-icon-arrow-right"
                :disabled="!mapReady"
                @click="moveOverlay('east')"
              />
            </el-tooltip>
            <el-tooltip content="向下移动" placement="top">
              <el-button
                icon="el-icon-arrow-down"
                :disabled="!mapReady"
                @click="moveOverlay('south')"
              />
            </el-tooltip>
            <el-tooltip content="放大图片" placement="top">
              <el-button
                icon="el-icon-zoom-in"
                :disabled="!mapReady"
                @click="scaleOverlay(1.02)"
              />
            </el-tooltip>
            <el-tooltip content="缩小图片" placement="top">
              <el-button
                icon="el-icon-zoom-out"
                :disabled="!mapReady"
                @click="scaleOverlay(0.98)"
              />
            </el-tooltip>
            <el-tooltip content="重置图片" placement="top">
              <el-button
                icon="el-icon-refresh-left"
                :disabled="!mapReady"
                @click="resetOverlay"
              />
            </el-tooltip>
          </div>
        </el-form>
      </div>
      <el-alert
        v-if="loadError"
        class="tiandi-map__error"
        :title="loadError"
        type="error"
        :closable="false"
        show-icon
      />
    </div>
  </div>
</template>

<script>
import * as maptalks from 'maptalks';
import 'maptalks/dist/maptalks.css';

const DEFAULT_TIANDITU_TOKEN = process.env.VUE_APP_TIANDITU_TOKEN || '63bed3ecf227260dcb4104960f2c4831';
const DEFAULT_TIANDITU_TILE_URL = 'https://t{s}.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';
const TIANDITU_TILE_SUBDOMAINS = ['0', '1', '2', '3', '4', '5', '6', '7'];
const DEFAULT_OVERLAY_IMAGE_URL = `${process.env.BASE_URL || '/'}cdn/tiandi/img/guochu-factory-overlay-transparent.png?v=20260724-inner-white`;
const OVERLAY_STORAGE_KEY = 'tiandi-map-guochu-overlay-inner-white';
const DEFAULT_OVERLAY_CENTER = [105.26089, 37.63015];
const DEFAULT_MARKERS = [
  {
    name: '大门正中间',
    coordinate: [105.2571946692, 37.62666108]
  },
  {
    name: '雨水收集池西南侧',
    coordinate: [105.2542189257, 37.62670934]
  },
  {
    name: '清扫去清扫棚西北角',
    coordinate: [105.2480300077, 37.63174947]
  },
  {
    name: '东大门中间',
    coordinate: [105.2664377318, 37.6264884]
  }
];
const DEFAULT_CENTER = DEFAULT_OVERLAY_CENTER;
const DEFAULT_OVERLAY_IMAGE_RATIO = 4096 / 1502;
const DEFAULT_OVERLAY_CENTER_LAT = DEFAULT_OVERLAY_CENTER[1];
const DEFAULT_OVERLAY_WIDTH = 0.0332;
const DEFAULT_OVERLAY_HEIGHT = 0.00961;
function getProportionalOverlayHeight(width, centerLat) {
  const numberWidth = Number(width);
  const numberLat = Number(centerLat);
  if (!Number.isFinite(numberWidth) || numberWidth <= 0) return 0.00932;
  const lat = Number.isFinite(numberLat) ? numberLat : DEFAULT_OVERLAY_CENTER_LAT;
  return numberWidth * Math.cos((lat * Math.PI) / 180) / DEFAULT_OVERLAY_IMAGE_RATIO;
}
const DEFAULT_OVERLAY_FORM = {
  visible: true,
  centerLng: DEFAULT_OVERLAY_CENTER[0],
  centerLat: DEFAULT_OVERLAY_CENTER_LAT,
  width: DEFAULT_OVERLAY_WIDTH,
  height: DEFAULT_OVERLAY_HEIGHT,
  opacity: 1,
  nudgeStep: 0.00005
};
export default {
  name: 'TiandiMap',
  props: {
    height: {
      type: String,
      default: '600px'
    },
    tiandituToken: {
      type: String,
      default: DEFAULT_TIANDITU_TOKEN
    },
    tileUrlTemplate: {
      type: String,
      default: DEFAULT_TIANDITU_TILE_URL
    },
    center: {
      type: Array,
      default: () => DEFAULT_CENTER.slice()
    },
    zoom: {
      type: Number,
      default: 18
    },
    initialMarkers: {
      type: Array,
      default: () => []
    },
    polygonPoints: {
      type: Array,
      default: () => []
    },
    persons: {
      type: Array,
      default: () => []
    },
    pathPoints: {
      type: Array,
      default: () => []
    },
    heatPoints: {
      type: Array,
      default: () => []
    },
    showPathMarkers: {
      type: Boolean,
      default: true
    },
    movingPoint: {
      type: [Array, Object],
      default: null
    },
    activePersonId: {
      type: [String, Number],
      default: ''
    },
    autoFit: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    overlayImageUrl: {
      type: String,
      default: DEFAULT_OVERLAY_IMAGE_URL
    },
    overlayConfig: {
      type: Object,
      default: () => ({})
    },
    showOverlayAdjuster: {
      type: Boolean,
      default: false
    },
    persistOverlayConfig: {
      type: Boolean,
      default: true
    },
    overlayStorageKey: {
      type: String,
      default: OVERLAY_STORAGE_KEY
    }
  },
  data() {
    const overlayForm = this.getInitialOverlayForm();

    return {
      loading: false,
      loadError: '',
      mapReady: false,
      coordinateForm: {
        longitude: '',
        latitude: ''
      },
      overlayForm
    };
  },
  watch: {
    overlayForm: {
      deep: true,
      handler() {
        this.updateImageOverlay();
      }
    },
    overlayImageUrl() {
      this.updateImageOverlay();
    },
    center: {
      deep: true,
      handler(value) {
        this.setCenter(value);
      }
    },
    zoom(value) {
      if (this.map && typeof value === 'number') {
        this.map.setZoom(value);
      }
    },
    persons: {
      deep: true,
      handler() {
        this.renderBusinessGeometries();
      }
    },
    pathPoints: {
      deep: true,
      handler() {
        this.renderBusinessGeometries();
      }
    },
    heatPoints: {
      deep: true,
      handler() {
        this.renderBusinessGeometries();
      }
    },
    showPathMarkers() {
      this.renderBusinessGeometries();
    },
    polygonPoints: {
      deep: true,
      handler() {
        this.renderBusinessGeometries();
      }
    },
    movingPoint: {
      deep: true,
      handler() {
        this.updateMovingMarker();
      }
    },
    activePersonId() {
      this.locatePerson(this.activePersonId);
    }
  },
  created() {
    this.map = null;
    this.imageLayer = null;
    this.vectorLayer = null;
    this.businessLayer = null;
    this.drawTool = null;
    this.resizeHandler = null;
    this.initialGeometries = [];
    this.personMarkers = {};
    this.movingMarker = null;
  },
  mounted() {
    this.initMap();
  },
  beforeDestroy() {
    this.destroyMap();
  },
  methods: {
    ensureMaptalksDragCompat() {
      if (typeof window === 'undefined' || !window.__POWERED_BY_QIANKUN__) return;
      if (typeof window.captureEvents !== 'function') return;

      const noopCaptureEvents = function noopCaptureEvents() {};
      try {
        Object.defineProperty(window, 'captureEvents', {
          configurable: true,
          writable: true,
          value: noopCaptureEvents
        });
      } catch (error) {
        try {
          window.captureEvents = noopCaptureEvents;
        } catch (assignError) {
          console.warn('[TiandiMap] patch captureEvents failed:', assignError || error);
        }
      }
    },
    initMap() {
      if (!this.$refs.mapContainer) return;

      this.ensureMaptalksDragCompat();
      this.loading = true;
      this.loadError = '';

      if (!this.tiandituToken) {
        this.loading = false;
        this.loadError = '未配置天地图密钥，请设置 VUE_APP_TIANDITU_TOKEN 或传入 tiandituToken';
        this.$emit('load-error', new Error(this.loadError));
        return;
      }

      try {
        const tileLayerOptions = this.getTileLayerOptions();

        this.map = new maptalks.Map(this.$refs.mapContainer, {
          center: this.normalizePoint(this.center),
          zoom: this.zoom,
          baseLayer: new maptalks.TileLayer('tiandi-tilelayer', tileLayerOptions),
          dragPitch: false,
          dragRotate: false
        });

        this.initImageOverlay();
        this.vectorLayer = new maptalks.VectorLayer('tiandi-vector').addTo(this.map);
        this.businessLayer = new maptalks.VectorLayer('tiandi-business').addTo(this.map);
        this.syncLayerZIndex();
        this.initDrawTool();
        this.bindMapEvents();
        this.renderInitialGeometries();
        this.renderBusinessGeometries();
        this.bindResize();

        this.mapReady = true;
        this.loading = false;
        this.$emit('loaded', this.map);
      } catch (error) {
        this.loading = false;
        this.loadError = '地图初始化失败';
        console.error('[TiandiMap] init failed:', error);
        this.$emit('load-error', error);
      }
    },
    initDrawTool() {
      this.drawTool = new maptalks.DrawTool({
        mode: 'Point'
      }).addTo(this.map).disable();

      this.drawTool.on('drawend', this.handleDrawEnd);
    },
    renderInitialGeometries() {
      this.initialGeometries = this.initialMarkers
        .map((point) => this.addMarker(point))
        .filter(Boolean);

      if (this.polygonPoints.length) {
        const polygon = this.addPolygon(this.polygonPoints);
        if (polygon) this.initialGeometries.push(polygon);
      }
    },
    bindResize() {
      this.resizeHandler = () => {
        if (this.map) {
          this.map.checkSize();
        }
      };
      window.addEventListener('resize', this.resizeHandler);
      this.$nextTick(this.resizeHandler);
    },
    destroyMap() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }

      if (this.drawTool) {
        this.drawTool.off('drawend', this.handleDrawEnd);
        this.drawTool.remove();
        this.drawTool = null;
      }

      if (this.map) {
        this.map.remove();
        this.map = null;
      }

      this.vectorLayer = null;
      this.businessLayer = null;
      this.imageLayer = null;
      this.initialGeometries = [];
      this.personMarkers = {};
      this.movingMarker = null;
      this.mapReady = false;
    },
    bindMapEvents() {
      if (!this.map) return;

      this.map.on('click', (event) => {
        if (!this.selectable) return;
        const coordinate = event.coordinate;
        if (!coordinate) return;
        const point = [coordinate.x, coordinate.y];
        this.$emit('map-click', {
          longitude: point[0],
          latitude: point[1],
          lnglat: point
        });
      });
    },
    initImageOverlay() {
      if (!this.overlayImageUrl || !this.overlayForm.visible) return;

      this.updateImageOverlay();
    },
    updateImageOverlay() {
      if (!this.map || !this.overlayImageUrl) return;

      if (!this.overlayForm.visible) {
        if (this.imageLayer) {
          this.imageLayer.hide();
        }
        this.saveOverlayConfig();
        this.$emit('overlay-change', this.getOverlayConfig());
        return;
      }

      if (!this.imageLayer) {
        this.imageLayer = new maptalks.ImageLayer(
          'tiandi-factory-overlay',
          [this.getOverlayImageItem()],
          {
            renderer: 'canvas'
          }
        ).addTo(this.map);
      }

      this.imageLayer.setImages([this.getOverlayImageItem()]);
      this.imageLayer.show();
      this.syncLayerZIndex();

      this.saveOverlayConfig();
      this.$emit('overlay-change', this.getOverlayConfig());
    },
    syncLayerZIndex() {
      if (this.imageLayer && this.imageLayer.setZIndex) {
        this.imageLayer.setZIndex(10);
      }
      if (this.vectorLayer && this.vectorLayer.setZIndex) {
        this.vectorLayer.setZIndex(20);
      }
      if (this.businessLayer && this.businessLayer.setZIndex) {
        this.businessLayer.setZIndex(30);
      }
    },
    getOverlayImageItem() {
      return {
        url: this.overlayImageUrl,
        extent: this.getOverlayExtent(),
        opacity: this.getOverlayOpacity()
      };
    },
    getOverlayExtent() {
      const centerLng = this.getFiniteOverlayNumber(this.overlayForm.centerLng, DEFAULT_OVERLAY_FORM.centerLng);
      const centerLat = this.getFiniteOverlayNumber(this.overlayForm.centerLat, DEFAULT_OVERLAY_FORM.centerLat);
      const width = Math.max(Number(this.overlayForm.width) || DEFAULT_OVERLAY_FORM.width, 0.000001);
      const height = Math.max(Number(this.overlayForm.height) || DEFAULT_OVERLAY_FORM.height, 0.000001);

      return [
        centerLng - width / 2,
        centerLat - height / 2,
        centerLng + width / 2,
        centerLat + height / 2
      ];
    },
    getOverlayOpacity() {
      const opacity = Number(this.overlayForm.opacity);
      if (!Number.isFinite(opacity)) return DEFAULT_OVERLAY_FORM.opacity;
      return Math.min(Math.max(opacity, 0), 1);
    },
    getOverlayConfig() {
      return {
        visible: !!this.overlayForm.visible,
        centerLng: Number(this.overlayForm.centerLng),
        centerLat: Number(this.overlayForm.centerLat),
        width: Number(this.overlayForm.width),
        height: Number(this.overlayForm.height),
        opacity: this.getOverlayOpacity(),
        nudgeStep: Number(this.overlayForm.nudgeStep)
      };
    },
    getInitialOverlayForm() {
      return Object.assign(
        {},
        DEFAULT_OVERLAY_FORM,
        this.overlayConfig,
        this.loadOverlayConfig()
      );
    },
    getOverlayStorageKey() {
      return this.overlayStorageKey || OVERLAY_STORAGE_KEY;
    },
    loadOverlayConfig() {
      if (!this.persistOverlayConfig || typeof window === 'undefined') return {};

      try {
        const cache = window.localStorage.getItem(this.getOverlayStorageKey());
        if (!cache) return {};
        const data = JSON.parse(cache);
        return data && typeof data === 'object' ? data : {};
      } catch (error) {
        console.warn('[TiandiMap] load overlay config failed:', error);
        return {};
      }
    },
    saveOverlayConfig() {
      if (!this.persistOverlayConfig || typeof window === 'undefined') return;

      try {
        window.localStorage.setItem(this.getOverlayStorageKey(), JSON.stringify(this.getOverlayConfig()));
      } catch (error) {
        console.warn('[TiandiMap] save overlay config failed:', error);
      }
    },
    moveOverlay(direction) {
      const step = Number(this.overlayForm.nudgeStep) || DEFAULT_OVERLAY_FORM.nudgeStep;
      const centerLng = this.getFiniteOverlayNumber(this.overlayForm.centerLng, DEFAULT_OVERLAY_FORM.centerLng);
      const centerLat = this.getFiniteOverlayNumber(this.overlayForm.centerLat, DEFAULT_OVERLAY_FORM.centerLat);
      const offsetMap = {
        north: [0, step],
        south: [0, -step],
        east: [step, 0],
        west: [-step, 0]
      };
      const offset = offsetMap[direction];
      if (!offset) return;

      this.overlayForm.centerLng = this.roundOverlayNumber(centerLng + offset[0], 10);
      this.overlayForm.centerLat = this.roundOverlayNumber(centerLat + offset[1], 10);
    },
    scaleOverlay(rate) {
      const width = this.getFiniteOverlayNumber(this.overlayForm.width, DEFAULT_OVERLAY_FORM.width);
      const height = this.getFiniteOverlayNumber(this.overlayForm.height, DEFAULT_OVERLAY_FORM.height);

      this.overlayForm.width = this.roundOverlayNumber(width * rate, 8);
      this.overlayForm.height = this.roundOverlayNumber(height * rate, 8);
    },
    resetOverlay() {
      this.overlayForm = Object.assign({}, DEFAULT_OVERLAY_FORM, this.overlayConfig);
      this.syncOverlayHeightByWidth();
    },
    roundOverlayNumber(value, precision) {
      const base = Math.pow(10, precision);
      return Math.round(value * base) / base;
    },
    getFiniteOverlayNumber(value, fallback) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : fallback;
    },
    syncOverlayHeightByWidth() {
      const width = this.getFiniteOverlayNumber(this.overlayForm.width, DEFAULT_OVERLAY_FORM.width);
      const centerLat = this.getFiniteOverlayNumber(this.overlayForm.centerLat, DEFAULT_OVERLAY_FORM.centerLat);
      this.overlayForm.height = this.roundOverlayNumber(
        getProportionalOverlayHeight(width, centerLat),
        8
      );
    },
    enableDrawPolygon() {
      if (!this.drawTool) return;

      this.drawTool.setMode('Polygon').enable();
    },
    handleDrawEnd(param) {
      this.vectorLayer.addGeometry(param.geometry);
      this.drawTool.disable();
      this.$emit('drawend', param.geometry);
    },
    clearLayer() {
      if (!this.vectorLayer && !this.businessLayer) return;

      if (this.vectorLayer) this.vectorLayer.clear();
      if (this.businessLayer) this.businessLayer.clear();
      this.initialGeometries = [];
      this.personMarkers = {};
      this.movingMarker = null;
      this.$emit('clear');
    },
    renderBusinessGeometries() {
      if (!this.businessLayer) return;

      this.businessLayer.clear();
      this.personMarkers = {};
      this.movingMarker = null;

      const geometries = [];
      geometries.push(...this.renderPersons());
      geometries.push(...this.renderPath());
      geometries.push(...this.renderHeatPoints());
      geometries.push(...this.renderBusinessPolygon());
      const moving = this.updateMovingMarker();
      if (moving) geometries.push(moving);

      const fitGeometries = geometries.concat(this.initialGeometries || []);
      if (this.autoFit && fitGeometries.length) {
        this.fitGeometries(fitGeometries);
      }
      if (this.activePersonId) {
        this.locatePerson(this.activePersonId);
      }
    },
    renderPersons() {
      return (this.persons || []).map((person) => {
        const coordinate = this.normalizePoint(person);
        if (!this.isValidLngLat(coordinate[0], coordinate[1])) return null;

        const id = String(person.id || person.employeeId || person.rtkId || `${coordinate[0]},${coordinate[1]}`);
        const marker = new maptalks.Marker(coordinate, {
          symbol: this.getPersonMarkerSymbol(person)
        }).addTo(this.businessLayer);
        const label = this.addBusinessLabel(this.getMarkerName(person) || person.rtkId || id, coordinate);
        marker.on('click', () => {
          this.$emit('person-click', Object.assign({}, person, { id }));
        });
        this.personMarkers[id] = marker;
        return label ? [marker, label] : marker;
      }).reduce((list, item) => {
        if (Array.isArray(item)) return list.concat(item);
        if (item) list.push(item);
        return list;
      }, []);
    },
    renderPath() {
      const coordinates = (this.pathPoints || [])
        .map((point) => this.normalizePoint(point))
        .filter((point) => this.isValidLngLat(point[0], point[1]));
      if (!coordinates.length) return [];

      const geometries = [];
      if (coordinates.length >= 2) {
        geometries.push(new maptalks.LineString(coordinates, {
          symbol: {
            lineColor: '#3366FF',
            lineWidth: 4,
            lineOpacity: 0.85
          }
        }).addTo(this.businessLayer));
      }
      if (this.showPathMarkers) {
        coordinates.forEach((coordinate, index) => {
          geometries.push(new maptalks.Marker(coordinate, {
            symbol: this.getOrderMarkerSymbol(index + 1)
          }).addTo(this.businessLayer));
        });
      }
      return geometries;
    },
    renderHeatPoints() {
      const values = (this.heatPoints || [])
        .map((point) => Number(point.value || point.count) || 0);
      const maxValue = Math.max.apply(null, values.concat([1]));

      return (this.heatPoints || []).map((point) => {
        const coordinate = this.normalizePoint(point);
        if (!this.isValidLngLat(coordinate[0], coordinate[1])) return null;

        const value = Number(point.value || point.count) || 0;
        const ratio = Math.max(0.15, Math.min(value / maxValue, 1));
        const size = 18 + ratio * 38;
        const marker = new maptalks.Marker(coordinate, {
          symbol: {
            markerType: 'ellipse',
            markerFill: this.getHeatColor(ratio),
            markerFillOpacity: 0.28 + ratio * 0.42,
            markerLineColor: this.getHeatColor(ratio),
            markerLineOpacity: 0.85,
            markerLineWidth: 1,
            markerWidth: size,
            markerHeight: size
          }
        }).addTo(this.businessLayer);
        const label = this.addBusinessLabel(point.name || String(value), coordinate, {
          textDy: -Math.max(18, size / 2)
        });
        return label ? [marker, label] : marker;
      }).reduce((list, item) => {
        if (Array.isArray(item)) return list.concat(item);
        if (item) list.push(item);
        return list;
      }, []);
    },
    renderBusinessPolygon() {
      if (!this.polygonPoints || !this.polygonPoints.length) return [];
      const polygon = this.addPolygonToLayer(this.polygonPoints, this.businessLayer);
      return polygon ? [polygon] : [];
    },
    updateMovingMarker() {
      if (!this.businessLayer || !this.movingPoint) return null;
      const coordinate = this.normalizePoint(this.movingPoint);
      if (!this.isValidLngLat(coordinate[0], coordinate[1])) return null;
      if (this.movingMarker) {
        this.movingMarker.setCoordinates(coordinate);
        return this.movingMarker;
      }
      this.movingMarker = new maptalks.Marker(coordinate, {
        symbol: {
          markerType: 'ellipse',
          markerFill: '#E6A23C',
          markerFillOpacity: 1,
          markerLineColor: '#ffffff',
          markerLineWidth: 2,
          markerWidth: 22,
          markerHeight: 22
        }
      }).addTo(this.businessLayer);
      return this.movingMarker;
    },
    addMarkerByForm() {
      const longitude = this.normalizeCoordinate(this.coordinateForm.longitude);
      const latitude = this.normalizeCoordinate(this.coordinateForm.latitude);

      if (!this.isValidLngLat(longitude, latitude)) {
        this.$message.warning('请输入有效的经纬度');
        return;
      }

      const marker = this.addMarker([longitude, latitude]);
      if (this.map) {
        this.map.setCenter([longitude, latitude]);
      }
      this.$emit('marker-add', { longitude, latitude });
      return marker;
    },
    addMarker(point, symbolOverrides = {}) {
      if (!this.vectorLayer) return null;

      const coordinate = this.normalizePoint(point);
      if (!this.isValidLngLat(coordinate[0], coordinate[1])) return null;

      const marker = new maptalks.Marker(coordinate, {
        symbol: Object.assign({}, this.getMarkerSymbol(), symbolOverrides)
      });
      marker.addTo(this.vectorLayer);
      this.addMarkerLabel(point, coordinate);
      return marker;
    },
    addMarkerLabel(point, coordinate) {
      const name = this.getMarkerName(point);
      if (!name) return null;

      return new maptalks.Label(name, coordinate, {
        symbol: {
          textFaceName: 'Microsoft YaHei, Arial, sans-serif',
          textSize: 14,
          textFill: '#303133',
          textHaloFill: '#ffffff',
          textHaloRadius: 2,
          textDx: 0,
          textDy: -28,
          textHorizontalAlignment: 'center',
          textVerticalAlignment: 'bottom'
        }
      }).addTo(this.vectorLayer);
    },
    addPolygon(points) {
      return this.addPolygonToLayer(points, this.vectorLayer);
    },
    addPolygonToLayer(points, layer) {
      if (!layer) return null;

      const coordinates = points
        .map((point) => this.normalizePoint(point))
        .filter((point) => this.isValidLngLat(point[0], point[1]));

      if (!coordinates.length) return null;

      if (coordinates.length === 1) {
        return new maptalks.Marker(coordinates[0], {
          symbol: this.getOrderMarkerSymbol(1)
        }).addTo(layer);
      }

      if (coordinates.length === 2) {
        return new maptalks.LineString(coordinates, {
          symbol: {
            lineColor: '#34495e',
            lineWidth: 2,
            lineOpacity: 0.85
          }
        }).addTo(layer);
      }

      return new maptalks.Polygon([coordinates], {
        visible: true,
        editable: true,
        cursor: 'pointer',
        draggable: false,
        dragShadow: false,
        drawOnAxis: null,
        symbol: {
          lineColor: '#34495e',
          lineWidth: 2,
          polygonFill: 'rgb(135,196,240)',
          polygonOpacity: 0.6
        }
      }).addTo(layer);
    },
    setCenter(point, zoom) {
      if (!this.map) return;

      this.map.setCenter(this.normalizePoint(point));
      if (typeof zoom === 'number') {
        this.map.setZoom(zoom);
      }
    },
    locatePerson(id) {
      const key = String(id || '');
      if (!key || !this.map || !this.personMarkers[key]) return;
      const marker = this.personMarkers[key];
      this.map.setCenter(marker.getCoordinates());
      this.map.setZoom(Math.max(this.map.getZoom(), 18));
    },
    fitGeometries(geometries) {
      if (!this.map || !geometries.length) return;
      const extents = geometries
        .map((geometry) => geometry && geometry.getExtent && geometry.getExtent())
        .filter(Boolean);
      if (!extents.length) return;
      const extent = extents.slice(1).reduce((acc, item) => acc.combine(item), extents[0]);
      if (extent && extent.isValid && extent.isValid()) {
        this.map.fitExtent(extent, 0);
      }
    },
    getTileLayerOptions() {
      return {
        urlTemplate: this.appendToken(this.tileUrlTemplate),
        subdomains: TIANDITU_TILE_SUBDOMAINS,
        cssFilter: 'saturate(1.3)'
      };
    },
    appendToken(url = '') {
      if (!this.tiandituToken || url.indexOf('tk=') > -1) {
        return url;
      }
      return `${url}${url.indexOf('?') > -1 ? '&' : '?'}tk=${this.tiandituToken}`;
    },
    normalizePoint(point) {
      if (Array.isArray(point)) {
        return [
          this.normalizeCoordinate(point[0]),
          this.normalizeCoordinate(point[1])
        ];
      }

      if (point && Array.isArray(point.coordinate)) {
        return this.normalizePoint(point.coordinate);
      }

      if (point && Array.isArray(point.coordinates)) {
        return this.normalizePoint(point.coordinates);
      }

      if (point && typeof point === 'object') {
        return [
          this.normalizeCoordinate(this.getPointValue(point, ['longitude', 'lng', 'lon', 'x'])),
          this.normalizeCoordinate(this.getPointValue(point, ['latitude', 'lat', 'y']))
        ];
      }

      return [
        NaN,
        NaN
      ];
    },
    getPointValue(point, keys) {
      for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index];
        if (point[key] !== undefined && point[key] !== null && point[key] !== '') {
          return point[key];
        }
      }
      return undefined;
    },
    getMarkerName(point) {
      if (!point || typeof point !== 'object' || Array.isArray(point)) {
        return '';
      }
      return point.name || point.employeeName || point.label || point.position || '';
    },
    normalizeCoordinate(value) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return NaN;
      }

      if (Math.abs(numberValue) >= 1000) {
        const degrees = parseInt(numberValue / 100, 10);
        const minutes = numberValue - degrees * 100;
        return degrees + minutes / 60;
      }

      return numberValue;
    },
    isValidLngLat(longitude, latitude) {
      return Number.isFinite(longitude)
        && Number.isFinite(latitude)
        && longitude >= -180
        && longitude <= 180
        && latitude >= -90
        && latitude <= 90;
    },
    getMarkerSymbol() {
      return {
        markerType: 'ellipse',
        markerFill: '#F56C6C',
        markerFillOpacity: 0.95,
        markerLineColor: '#ffffff',
        markerLineWidth: 2,
        markerWidth: 18,
        markerHeight: 18,
        markerDx: 0,
        markerDy: 0,
        markerOpacity: 1
      };
    },
    getPersonMarkerSymbol(person) {
      const online = person.online !== false && person.status !== 'offline';
      const busy = !!person.inInspection || person.status === 'busy';
      return {
        markerType: 'ellipse',
        markerFill: online ? (busy ? '#F59E0B' : '#22C55E') : '#A8ABB2',
        markerFillOpacity: 0.95,
        markerLineColor: '#ffffff',
        markerLineWidth: 2,
        markerWidth: 18,
        markerHeight: 18
      };
    },
    getOrderMarkerSymbol(order) {
      return {
        markerType: 'ellipse',
        markerFill: '#ffffff',
        markerLineColor: '#3366FF',
        markerLineWidth: 3,
        markerWidth: 26,
        markerHeight: 26,
        textName: String(order),
        textFill: '#3366FF',
        textSize: 12,
        textWeight: 'bold',
        textDy: 1
      };
    },
    getHeatColor(ratio) {
      if (ratio >= 0.8) return '#01579b';
      if (ratio >= 0.55) return '#039be5';
      if (ratio >= 0.35) return '#29b6f6';
      return '#81d4fa';
    },
    addBusinessLabel(name, coordinate, symbolOverrides = {}) {
      if (!name || !this.businessLayer) return null;
      return new maptalks.Label(String(name), coordinate, {
        symbol: Object.assign({
          textFaceName: 'Microsoft YaHei, Arial, sans-serif',
          textSize: 12,
          textFill: '#303133',
          textHaloFill: '#ffffff',
          textHaloRadius: 2,
          textDy: -24,
          textHorizontalAlignment: 'center',
          textVerticalAlignment: 'bottom'
        }, symbolOverrides)
      }).addTo(this.businessLayer);
    }
  }
};
</script>

<style scoped>
.tiandi-map {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 420px;
  background: #fff;
}

.tiandi-map__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-bottom: 0;
  background: #fff;
}

.tiandi-map__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.tiandi-map__form {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-left: 16px;
}

.tiandi-map__form ::v-deep .el-form-item {
  margin-bottom: 0;
}

.tiandi-map__form ::v-deep .el-input {
  width: 180px;
}

.tiandi-map__body {
  position: relative;
  flex: 1;
  min-height: 0;
  border: 1px solid #ebeef5;
}

.tiandi-map__container {
  width: 100%;
  height: 100%;
}

.tiandi-map__overlay-panel {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  width: 292px;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}

.tiandi-map__overlay-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.tiandi-map__overlay-form .el-form-item {
  margin-bottom: 8px;
}

.tiandi-map__overlay-form .el-input-number {
  width: 100%;
}

.tiandi-map__overlay-tools {
  display: grid;
  grid-template-columns: repeat(7, 32px);
  gap: 6px;
  justify-content: space-between;
  margin-top: 4px;
}

.tiandi-map__overlay-tools .el-button {
  width: 32px;
  height: 28px;
  padding: 0;
}

.tiandi-map__error {
  position: absolute;
  left: 16px;
  right: 16px;
  top: 16px;
  z-index: 2;
}

@media (max-width: 768px) {
  .tiandi-map {
    height: auto !important;
  }

  .tiandi-map__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .tiandi-map__form {
    justify-content: flex-start;
    width: 100%;
    margin-top: 12px;
    margin-left: 0;
  }

  .tiandi-map__form ::v-deep .el-form-item {
    margin-bottom: 10px;
  }

  .tiandi-map__body {
    height: 420px;
    flex: none;
  }

  .tiandi-map__overlay-panel {
    left: 12px;
    right: 12px;
    width: auto;
  }
}
</style>
